import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastError, errorMessage } from '../../../lib/toast';
import { StatusPill } from './appStatus';

const ALL_STAGE_STATUSES = ['approved', 'provisioning', 'active', 'publish_requested', 'published'];

// Lists apps for a set of lifecycle statuses (admin Apps stage tabs). When no
// `statuses` prop is given it shows every post-pending stage. `onCountChange`
// reports the row count back for tab badges.
export default function ActiveAppsList({ statuses = ALL_STAGE_STATUSES, emptyText = 'No apps in this stage yet.', onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const mountedRef = useRef(true);
  // Set true on (re)mount, not just at declaration — React 18 StrictMode dev
  // double-mounts, and a declaration-only `useRef(true)` stays false after the
  // first cleanup, which would strand guarded setState (e.g. setLoading(false)).
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const key = statuses.join(',');
  const load = useCallback(async () => {
    try {
      const list = key.split(',');
      const results = await Promise.all(list.map((s) =>
        axios.get(`${API_BASE_URL}/api/admin/app_requests`, { params: { status: s, size: 100 }, withCredentials: true })
      ));
      const merged = results.flatMap((r) => r.data.rows);
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (mountedRef.current) {
        setRows(merged);
        if (onCountChange) onCountChange(merged.length);
      }
    } catch (err) {
      toastError(errorMessage(err, 'Couldn’t load apps. Try again.'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [key, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const awaitingProvision = rows.filter((r) => r.status === 'approved' || r.status === 'provisioning').length;
  const awaitingPublish = rows.filter((r) => r.status === 'publish_requested').length;

  return (
    <div>
      {!loading && awaitingPublish > 0 && (
        <div className="apps-stage-banner apps-stage-banner--publish">
          {awaitingPublish} app{awaitingPublish === 1 ? '' : 's'} awaiting your publish review — open to publish or request changes.
        </div>
      )}

      {!loading && awaitingProvision > 0 && (
        <div className="apps-stage-banner apps-stage-banner--provision">
          {awaitingProvision} app{awaitingProvision === 1 ? '' : 's'} awaiting provisioning — assign AWS Parameter Store references to activate.
        </div>
      )}

      <div className="admin-table-count" style={{ marginBottom: 8 }}>
        {loading ? 'Loading…' : `${rows.length} app${rows.length === 1 ? '' : 's'}`}
      </div>

      {!loading && rows.length === 0 ? (
        <div className="admin-table-wrap" style={{ textAlign: 'center', padding: 48, color: '#718096' }}>
          {emptyText}
        </div>
      ) : !loading && (
        <div className="admin-table-wrap ut-tablewrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>App</th>
                <th>Requester</th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 90 }}>Tools</th>
                <th style={{ width: 150, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const needsProvision = r.status === 'approved' || r.status === 'provisioning';
                const needsPublishReview = r.status === 'publish_requested';
                const actionLabel = needsProvision ? 'Provision' : needsPublishReview ? 'Review publish' : 'View';
                const actionPrimary = needsProvision || needsPublishReview;
                return (
                  <tr key={r.id}>
                    <td><div className="at-primary">{r.name}</div></td>
                    <td><div className="at-secondary">{r.requester_name}</div></td>
                    <td>
                      <StatusPill status={r.status} />
                      {r.status !== 'published' && r.published_tool_id && <span className="apps-published-tag">Published</span>}
                    </td>
                    <td className="at-muted">{r.tool_count}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className={`btn btn-small ${actionPrimary ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => navigate(`/admin/apps/${r.id}`)}
                      >
                        {actionLabel}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
